import { ApolloError } from '@apollo/client';
import { AxiosRequestConfig } from 'axios';
import { RelationshipFetchPolicy } from 'redux/actions/GraphQlActions/interface';
import { UserInstance } from 'types/interfaces';
import { BugTracker } from 'Utils/Bugtracker';
import { dbQueue } from '../Interceptor';
import { queryClient } from 'queryClient';
import { cosneticsQueryKeys } from '../cosneticsQueryKeys';
import { normalizeRelationshipItem } from '../User Instance API/normalizers';
import { isCosneticsAuthenticationError } from '../cosneticsAuthentication';

const reportUnexpectedError = (error: unknown) => {
  if (!isCosneticsAuthenticationError(error)) BugTracker.notify(error);
};

/**
 * RELATIONSHIPS API
 */

export type RelationShipActions =
  | 'LISTFORKEYWORD'
  | 'DELETE'
  | 'POST'
  | 'LIST_ALL'
  | 'LIST'
  | 'RELATEDTOASSIGNED'
  | 'CREATE';

interface IRelationshipInput {
  PrimaryUserInstanceId: number;
  RelatedUserInstanceId: number;
  RelatedUserPermission: TRelatedUserPermission;
  Status: number;
  Title: string;
}

const fetchRelationshipQuery = async ({
  baseUrl,
  queryKey,
  fetchPolicy,
  url
}: {
  baseUrl: string;
  queryKey: readonly unknown[];
  fetchPolicy?: RelationshipFetchPolicy;
  url: string;
}): Promise<UserInstance[]> => {
  const request = async (): Promise<UserInstance[]> => {
    const config: AxiosRequestConfig = { method: 'get', url, baseURL: baseUrl };
    const response = await dbQueue({ config, url, throwOnAuthError: true });
    const payload = response?.data?.Items ?? response?.data;

    if (!Array.isArray(payload)) {
      throw new Error('No relationship data returned');
    }

    return payload.map(normalizeRelationshipItem) as unknown as UserInstance[];
  };

  if (fetchPolicy === 'no-cache') return request();

  if (fetchPolicy === true || fetchPolicy === 'network-only') {
    const result = await request();
    queryClient.setQueryData(queryKey, result);
    return result;
  }

  return queryClient.fetchQuery(queryKey, request, { staleTime: Infinity });
};

export type TRelatedUserPermission = { Standard; Read; Write };
export interface RelationshipsAPIInterface {
  action: RelationShipActions;
  baseUrl: string;
  AssignedUserDefinitionId?: number;
  ProcessInstanceId?: number;
  RelatedUserDefinitionId?: number;
  UserInstanceId?: number;
  UserDefinitionId?: number;
  PrimaryUserInstanceId?: number;
  RelatedUserInstanceId?: number;
  keyword?: string | null;
  Title?: string | null;
  data?: IRelationshipInput[];
  Status?: number | null;
}

/**
 * Retrieves a list of UserInstances related to a specific keyword.
 *
 * @param {string} baseUrl - The base URL for the Hostname.
 * @param {string} action - Action type, expected to be 'LISTFORKEYWORD'.
 * @param {number} UserInstanceId - The UserInstanceId for initiating the request.
 * @param {string} keyword - The keyword used to filter UserInstances.
 * @returns {Promise<UserInstance[] | ApolloError>} A promise that resolves with an array of UserInstances or an ApolloError in case of failure.
 */

const GetListForKeywordRelationship = async ({
  baseUrl,
  action,
  UserInstanceId,
  keyword
}: {
  baseUrl: string;
  action: 'LISTFORKEYWORD';
  UserInstanceId: number;
  keyword: string;
}): Promise<UserInstance[] | ApolloError> => {
  try {
    const queryKey = cosneticsQueryKeys.relationships({
      action,
      baseUrl,
      UserInstanceId,
      keyword
    });
    const params = new URLSearchParams({
      action,
      userinstanceid: UserInstanceId.toString(),
      keyword
    });
    return await fetchRelationshipQuery({
      baseUrl,
      queryKey,
      url: `RelationshipAPI.ashx?${params.toString()}`
    });
  } catch (e) {
    reportUnexpectedError(e);

    const error = e as Error;
    const errorMessage = error.message || 'An unexpected error occurred';
    return new ApolloError({ errorMessage });
  }
};

/**
 * Fetches UserInstances that have a specified relationship between assigned and related UserDefinition.
 *
 * @param {string} baseUrl - The base URL for the Hostname.
 * @param {string} action - Action type, expected to be 'RELATEDTOASSIGNED'.
 * @param {number} ProcessInstanceId - The ProcessInstanceId associated with the request.
 * @param {number} AssignedUserDefinitionId - The UserDefinitionId for the assigned user.
 * @param {number} RelatedUserDefinitionId - The UserDefinitionId for the related user.
 * @returns {Promise<UserInstance[] | ApolloError>} A promise that resolves with an array of UserInstances or an ApolloError in case of failure.
 */

const GetRelatedToAssignedRelationship = async ({
  baseUrl,
  action,
  ProcessInstanceId,
  AssignedUserDefinitionId,
  RelatedUserDefinitionId,
  fetchPolicy
}: {
  baseUrl: string;
  action: 'RELATEDTOASSIGNED';
  ProcessInstanceId: number;
  AssignedUserDefinitionId: number;
  RelatedUserDefinitionId: number;
  fetchPolicy?: RelationshipFetchPolicy;
}): Promise<UserInstance[] | ApolloError> => {
  try {
    const queryKey = cosneticsQueryKeys.relationships({
      action,
      AssignedUserDefinitionId,
      baseUrl,
      ProcessInstanceId,
      RelatedUserDefinitionId
    });
    const params = new URLSearchParams({
      action,
      processinstanceid: ProcessInstanceId.toString(),
      assigneduserdefinitionid: AssignedUserDefinitionId.toString(),
      relateduserdefinitionid: RelatedUserDefinitionId.toString()
    });
    return await fetchRelationshipQuery({
      baseUrl,
      fetchPolicy,
      queryKey,
      url: `RelationshipAPI.ashx?${params.toString()}`
    });
  } catch (e) {
    reportUnexpectedError(e);

    const error = e as Error;
    const errorMessage = error.message || 'An unexpected error occurred';
    return new ApolloError({ errorMessage });
  }
};

/**
 * Retrieves a list of UserInstances based on the UserInstancesId and UserDefinitionId.
 *
 * @param {string} baseUrl - The base URL for the Hostname.
 * @param {string} action - Action type, expected to be 'LIST'.
 * @param {number} UserInstanceId - The UserInstanceId initiating the request.
 * @param {number} UserDefinitionId - The UserDefinitionId used to filter UserInstances.
 * @returns {Promise<UserInstance[] | ApolloError>} A promise that resolves with an array of UserInstances or an ApolloError in case of failure.
 */

const GetRelationshipList = async ({
  baseUrl,
  action,
  UserInstanceId,
  UserDefinitionId,
  fetchPolicy
}: {
  baseUrl: string;
  action: 'LIST' | 'LIST_ALL';
  UserInstanceId: number;
  UserDefinitionId: number;
  fetchPolicy?: RelationshipFetchPolicy;
}): Promise<UserInstance[] | ApolloError> => {
  try {
    const queryKey = cosneticsQueryKeys.relationships({
      action,
      baseUrl,
      UserDefinitionId,
      UserInstanceId
    });
    const params = new URLSearchParams({
      action: action === 'LIST_ALL' ? 'LIST-ALL' : action,
      userinstanceid: UserInstanceId.toString(),
      userdefinitionid: UserDefinitionId.toString()
    });
    return await fetchRelationshipQuery({
      baseUrl,
      fetchPolicy,
      queryKey,
      url: `RelationshipAPI.ashx?${params.toString()}`
    });
  } catch (e) {
    reportUnexpectedError(e);

    const error = e as Error;
    const errorMessage = error.message || 'An unexpected error occurred';
    return new ApolloError({ errorMessage });
  }
};

export {
  GetListForKeywordRelationship,
  GetRelatedToAssignedRelationship,
  GetRelationshipList
};
